
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";

interface OvertimePaymentProps {
  booking: {
    id: string;
    overtime_hours: number;
    overtime_charges: number;
    user?: {
      first_name?: string;
      last_name?: string;
      phone?: string;
    };
  };
}

export const OvertimePayment = ({ booking }: OvertimePaymentProps) => {
  const [processingPayment, setProcessingPayment] = useState(false);
  const { toast } = useToast();

  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = resolve;
      script.onerror = () => reject(new Error("Failed to load Razorpay script"));
      document.body.appendChild(script);
    });
  };

  const handleOvertimePayment = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setProcessingPayment(true);
    
    try {
      console.log('Loading Razorpay script...');
      await loadRazorpayScript();
      console.log('Razorpay script loaded successfully');
      
      console.log('Invoking charge-overtime function with booking ID:', booking.id);
      const { data: response, error } = await supabase.functions.invoke('charge-overtime', {
        body: { bookingId: booking.id }
      });

      if (error) {
        console.error('Error invoking charge-overtime function:', error);
        throw error;
      }

      console.log('Response from charge-overtime:', response);
      if (!response || !response.orderId) {
        throw new Error('Invalid response from payment service');
      }

      const options = {
        key: response.keyId,
        amount: response.amount * 100,
        currency: response.currency,
        name: "Parking Overtime Charges",
        description: `Overtime charges for booking ${booking.id}`,
        order_id: response.orderId,
        handler: async function (response: any) {
          console.log("Payment successful:", response);
          
          const { error: updateError } = await supabase
            .from('bookings')
            .update({
              overtime_payment_status: 'paid',
              updated_at: new Date().toISOString()
            })
            .eq('id', booking.id);

          if (updateError) {
            console.error("Error updating payment status:", updateError);
            toast({
              title: "Warning",
              description: "Payment successful but status update failed. Please contact support.",
              variant: "destructive",
            });
            return;
          }

          toast({
            title: "Payment Successful",
            description: "Overtime charges have been paid successfully.",
          });
        },
        modal: {
          ondismiss: function() {
            console.log("Payment cancelled");
            toast({
              title: "Payment Cancelled",
              description: "Overtime charges payment was cancelled.",
              variant: "destructive",
            });
            setProcessingPayment(false);
          }
        },
        prefill: {
          name: booking.user ? `${booking.user.first_name || ''} ${booking.user.last_name || ''}`.trim() : '',
          contact: booking.user?.phone || ''
        },
        theme: {
          color: "#3B82F6"
        }
      };

      console.log("Initializing Razorpay with options:", options);
      const razorpay = new (window as any).Razorpay(options);
      razorpay.open();

    } catch (error: any) {
      console.error("Error initializing payment:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to initialize payment",
        variant: "destructive",
      });
      setProcessingPayment(false);
    }
  };

  return (
    <Button
      onClick={handleOvertimePayment}
      className="mt-2 w-full"
      disabled={processingPayment}
    >
      {processingPayment ? "Processing..." : "Pay Overtime Charges"}
    </Button>
  );
};
